import { Telescope } from "lucide-react"

import { cn } from "@/lib/utils"
import type { RetrievalEngine } from "@/types/api"

interface RetrievalSwitchProps {
  /** Engines this deployment can run. Fewer than two → no control. */
  engines: RetrievalEngine[]
  selectedId: string | null
  onSelect: (id: string) => void
}

/**
 * Picks which engine answers SharePoint questions this turn.
 *
 * Both engines run on the user's own delegated Graph token, so the choice never
 * changes *which documents* are reachable — only how relevance is found. That
 * is why this is presented as a search-quality control, not a permission one.
 */
export function RetrievalSwitch({ engines, selectedId, onSelect }: RetrievalSwitchProps) {
  if (engines.length < 2) return null

  return (
    <div className="flex max-w-fit items-center gap-2.5 self-start rounded-full border border-border bg-card py-1.5 pr-1.5 pl-3">
      <Telescope className="size-3.5 shrink-0 text-fg3" />
      <span className="shrink-0 text-xs text-fg3">Retrieval</span>
      <div role="radiogroup" aria-label="Retrieval engine" className="flex items-center gap-1">
        {engines.map((engine) => {
          const active = engine.id === selectedId
          return (
            <button
              key={engine.id}
              type="button"
              role="radio"
              aria-checked={active}
              title={engine.description}
              onClick={() => onSelect(engine.id)}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                active
                  ? "border-orange/35 bg-orange-tint text-fg1"
                  : "border-transparent text-fg3 hover:bg-elevated hover:text-foreground",
              )}
            >
              {engine.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
